import { useCallback, useEffect, useRef, useState } from "react";

export function useHarvestEntryRows({
  orgId,
  siteId,
  cropId,
  greenhouseData,
  selectedGreenhouses,
  setSelectedRowIds,
  setBayRowFilter,
  resetHistoryRef,
  naturalCompare,
}) {
  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [activeGreenhouseIds, setActiveGreenhouseIds] = useState([]);

  useEffect(() => {
    if (!greenhouseData || greenhouseData.length === 0) {
      setActiveGreenhouseIds([]);
      return;
    }
    const ids = greenhouseData
      .filter((g) => selectedGreenhouses.includes(g.name))
      .map((g) => g.id);
    setActiveGreenhouseIds(ids);
  }, [greenhouseData, selectedGreenhouses]);

  const rebuildRowsFromGreenhouseData = useCallback(() => {
    if (!orgId || !siteId || !cropId || !activeGreenhouseIds.length) {
      setRows([]);
      setSelectedRowIds(new Set());
      setBayRowFilter([]);

      // Clear history on dataset reset
      resetHistoryRef?.current?.();
      return;
    }

    const prevById = new Map();
    for (const r of rowsRef.current || []) prevById.set(r.id, r);

    const activeSet = new Set(activeGreenhouseIds);
    const out = [];

    for (const gh of greenhouseData || []) {
      if (!activeSet.has(gh.id)) continue;

      const bayName = gh.name;
      const greenhouseName = gh.name;

      for (const p of gh.plots || []) {
        const id = `${gh.id}-${p.id}`;
        const prev = prevById.get(id);

        const isHarvested = !!p.harvested_date;

        out.push({
          id,
          plot_id: p.id,
          greenhouse_id: gh.id,
          greenhouse_name: greenhouseName,
          bay_name: bayName,
          row_name: p.name,
          plot_name: p.name,

          is_harvested: isHarvested,
          harvest_date: p.harvested_date ?? null,
          yield: p.total_yield ?? 0,
          weight: p.total_weight ?? 0,
          weight_per_tub: p.weight_per_tub ?? null,
          weight_per_tub_source: p.weight_per_tub_source ?? null,
          ploughed_under_reasons: p.ploughed_under_reasons ?? [],
          ploughed_under_note: p.ploughed_under_note ?? null,

          pendingHarvestDate: prev?.pendingHarvestDate ?? null,
          pendingYield: prev?.pendingYield ?? null,
          pendingWeight: prev?.pendingWeight ?? null,
          pendingUnharvest: prev?.pendingUnharvest ?? false,
          pendingTubWeight: prev?.pendingTubWeight ?? null,
          pendingPloughedReasons: prev?.pendingPloughedReasons ?? null,
          pendingPloughedNote: prev?.pendingPloughedNote ?? null,
        });
      }
    }

    out.sort((a, b) => {
      const ghCmp = naturalCompare(a.greenhouse_name, b.greenhouse_name);
      if (ghCmp !== 0) return ghCmp;
      return naturalCompare(a.row_name, b.row_name);
    });

    setRows(out);

    // Keep selection only for still-existing rows
    const liveIds = new Set(out.map((r) => r.id));
    setSelectedRowIds((prev) => {
      const next = new Set();
      for (const id of prev || []) if (liveIds.has(id)) next.add(id);
      return next;
    });

    // Keep bay/row filter as-is (safe), but reset history (refresh changes base state)
    resetHistoryRef?.current?.();
  }, [
    activeGreenhouseIds,
    cropId,
    greenhouseData,
    naturalCompare,
    orgId,
    resetHistoryRef,
    setBayRowFilter,
    setSelectedRowIds,
    siteId,
  ]);

  useEffect(() => {
    rebuildRowsFromGreenhouseData();
  }, [rebuildRowsFromGreenhouseData]);

  return {
    rows,
    setRows,
    rowsRef,
    activeGreenhouseIds,
  };
}
